import dayjs from 'dayjs';

export const DATE_RANGE_OPTIONS = [
  { value: 'all', label: 'All Dates' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'today', label: 'Today' },
  { value: 'this-weekend', label: 'This Weekend' },
  { value: 'next-weekend', label: 'Next Weekend' },
  { value: 'this-week', label: 'This Week' },
  { value: 'next-14-days', label: 'Next 14 Days' },
  { value: 'custom', label: 'Custom Date Range' },
];

export const RELATIVE_DATE_PRESETS = new Set(
  DATE_RANGE_OPTIONS
    .map(({ value }) => value)
    .filter((value) => value !== 'all' && value !== 'custom')
);

export function getPresetDateRange(preset, now = dayjs()) {
  const today = now.startOf('day');
  const daysUntilSaturday = today.day() === 0 ? 0 : (6 - today.day() + 7) % 7;
  const thisSaturday = today.add(daysUntilSaturday, 'day');

  switch (preset) {
    case 'tomorrow': {
      const tomorrow = today.add(1, 'day');
      return { start: tomorrow, end: tomorrow };
    }
    case 'today':
      return { start: today, end: today };
    case 'this-weekend':
      return today.day() === 0
        ? { start: today, end: today }
        : { start: thisSaturday, end: thisSaturday.add(1, 'day') };
    case 'next-weekend': {
      const nextSaturday = thisSaturday.add(7, 'day');
      return { start: nextSaturday, end: nextSaturday.add(1, 'day') };
    }
    case 'this-week':
      // "This Week" means the remaining days through Sunday, regardless of
      // Day.js locale settings (whose default week ends on Saturday).
      return { start: today, end: today.add((7 - today.day()) % 7, 'day') };
    case 'next-14-days':
      return { start: today, end: today.add(13, 'day') };
    default:
      return { start: null, end: null };
  }
}

export function updateDateRangeSearch(search, start, end, preset = 'custom') {
  const params = new URLSearchParams(search);

  // Replace legacy or stale date parameters with one canonical representation.
  params.delete('date');
  params.delete('datePreset');
  params.delete('dateStart');
  params.delete('dateEnd');

  if (RELATIVE_DATE_PRESETS.has(preset)) {
    params.set('datePreset', preset);
  } else if (start || end) {
    // Custom and legacy absolute ranges remain fixed to calendar dates.
    if (start) {
      params.set('dateStart', start.format('YYYY-MM-DD'));
    }
    if (end) {
      params.set('dateEnd', end.format('YYYY-MM-DD'));
    }
  }

  const query = params.toString();
  return query ? `?${query}` : '';
}

export function getDateRangeFromSearch(search, now = dayjs()) {
  const params = new URLSearchParams(search);
  const preset = params.get('datePreset');

  if (RELATIVE_DATE_PRESETS.has(preset)) {
    return {
      ...getPresetDateRange(preset, now),
      preset,
    };
  }

  const legacyDate = params.get('date');
  const startValue = params.get('dateStart') || legacyDate;
  const endValue = params.get('dateEnd') || legacyDate;

  const parseDate = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
    const parsed = dayjs(value);
    return parsed.isValid() && parsed.format('YYYY-MM-DD') === value
      ? parsed.startOf('day')
      : null;
  };

  const start = parseDate(startValue);
  const end = parseDate(endValue);

  return {
    start,
    end,
    preset: start || end ? 'custom' : 'all',
  };
}

/*
 * Kept below the URL helpers because callers often use all three together:
 * parse the URL, calculate a relative range, then test event membership.
 */
export function isDateInRange(date, start, end) {
  const value = dayjs(date);
  if (!value.isValid()) return false;
  if (start && value.isBefore(start, 'day')) return false;
  if (end && value.isAfter(end, 'day')) return false;
  return true;
}
